/**
 * Helpers for the named price catalog used by the bulk pricing job builder.
 *
 * A named price is a price record shared across products: a store defines "small extra cheese" once
 * and every small pizza that resolves to it picks the price up, instead of each product/variant
 * carrying its own price ranges.
 */

/**
 * The amount an unpriced named price definition carries at the organization level. It is a sentinel,
 * not $99.99, and must never be shown as a price.
 */
export const UNPRICED_NAMED_PRICE_AMOUNT = 9999;

export const isUnpricedNamedPrice = (amount?: number | null): boolean =>
  amount == null || amount === UNPRICED_NAMED_PRICE_AMOUNT;

/** Formats whole cents as a dollar amount. No sentinel handling - see formatNamedPriceAmount. */
export const formatCents = (amount: number): string => `$${(amount / 100).toFixed(2)}`;

/**
 * Parses a price the user typed into whole cents. Returns null for anything that is not a
 * non-negative whole number.
 *
 * The entered value is whole cents, used verbatim - `250` is $2.50, not $250. That is how the rest of
 * the bulk-update forms treat `authoringPrice.amount` (see `parseCents` in dealLovers.utils, which
 * follows the same convention), so an editor here must not silently disagree with the form next to it.
 *
 * Digits only, deliberately. `Number('1.00')` is `1`, so a decimal entry would otherwise be accepted
 * at a hundredth of what it looks like - someone typing `1.00` for a dollar would set one cent and
 * get no warning. In a cents field a decimal point is always a mistake, so it is rejected rather than
 * rounded or reinterpreted. This also turns away the other shapes `Number` tolerates: `1e3`, `0x10`,
 * `+5`, `Infinity`.
 */
export const parseCentsInput = (value: string): number | null => {
  if (value == null) return null;
  const cleaned = value.trim();
  if (!/^\d+$/.test(cleaned)) return null;
  const cents = Number(cleaned);
  return Number.isSafeInteger(cents) ? cents : null;
};

/**
 * Renders a named price amount for display. Amounts are whole cents; the unpriced sentinel and a
 * missing amount both render as inherited rather than as a dollar value.
 */
export const formatNamedPriceAmount = (amount?: number | null): string =>
  isUnpricedNamedPrice(amount) ? 'Not set' : formatCents(amount as number);

/** A store's current named price override, as it exists on that store's patch. */
export interface CurrentNamedPrice {
  namedPriceId: string;
  name: string;
  amount?: number;
  /** The operation this override came from, when the payload identifies it. */
  patchOperationId?: string;
}

const SET_NAMED_PRICE_AMOUNT_TYPENAME = 'SetNamedPriceAmountPatchOperation';

/**
 * Resolves a named price id to its catalog display name, or undefined when the id is not in the
 * catalog. Supplied by the caller so this module stays free of data fetching.
 */
export type NamedPriceNameResolver = (namedPriceId: string) => string | undefined;

/**
 * Pulls a store's current named price overrides out of a loaded patch.
 *
 * The amounts here are store prices set by an earlier job - the only place the current value is
 * visible, since a named price is not part of the menu tree. Later operations win, matching how the
 * patch itself resolves duplicates.
 *
 * Names come from the catalog first when a resolver is given, because the patch payload is not
 * guaranteed to embed one. The id is the last resort: unhelpful to read, but it identifies the
 * record, which a placeholder string does not.
 */
export const extractCurrentNamedPrices = (
  patchData?: any,
  resolveName?: NamedPriceNameResolver
): CurrentNamedPrice[] => {
  const edges = patchData?.patchOperations?.edges;
  if (!Array.isArray(edges)) return [];

  const byId = new Map<string, CurrentNamedPrice>();

  edges.forEach((edge: any) => {
    const node = edge?.node;
    if (node?.__typename !== SET_NAMED_PRICE_AMOUNT_TYPENAME) return;

    const namedPriceId = node.namedPrice?.namedPriceId || node.namedPriceId;
    if (!namedPriceId) return;

    byId.set(namedPriceId, {
      namedPriceId,
      name:
        resolveName?.(namedPriceId) ||
        node.namedPrice?.name ||
        node.namedPrice?.internalName ||
        namedPriceId,
      amount: node.amount,
      patchOperationId: node.patchOperationId
    });
  });

  return Array.from(byId.values()).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
};
